import { BwicSavedFilter } from '../../../../types/filters/BwicSavedFilter';
import { FilterOption } from '../../../../types/filters/FilterOption';
import { BwicFilterType, CommonFilter, TFilterType } from '../../../../types/filters/FilterState';
import { LoanMonitorSavedFilter } from '../../../../types/filters/LoanMonitorSavedFilter';
import { BwicUserFilterValues, LoanUserFilterValues, UserFilterValues } from '../../../../types/user-config/UserConfigFilter';
import { deserializeBooleanFilter, deserializeDateFilter, deserializeDateTimeFilter, deserializePeriodEnd, deserializeRadioFilter, deserializeRangeFilter, deserializeSelectFilter } from '../amr-pipeline/filterDeserializers';
import { user } from '../../../../user';
import { roles } from '../../../../constants';

export const deserializeBwicSavedFilter = (commonFilter: UserFilterValues, stateCommonFilter: CommonFilter, filterType: TFilterType) =>
    filterType === BwicFilterType.LoanMonitor
        ? deserializeLoanMonitorFilter(commonFilter, stateCommonFilter)
        : deserializeBwicFilter(commonFilter, stateCommonFilter);

export const deserializeBwicFilter = (commonFilter: UserFilterValues, stateCommonFilter: CommonFilter) => {
    const filter = commonFilter as BwicUserFilterValues;
    const stateFilter = stateCommonFilter as BwicSavedFilter;

    return {
        date: stateFilter.date && deserializeDateFilter(filter.date),
        size: stateFilter.size && deserializeRangeFilter(filter.size),
        ratings: stateFilter.ratings && deserializeSelectFilter(stateFilter.ratings.filter, filter.ratings),
        statuses: stateFilter.statuses && deserializeSelectFilter(stateFilter.statuses.filter, filter.statuses),
        dealerIds: stateFilter.dealerIds && deserializeSelectFilter(stateFilter.dealerIds.filter, filter.dealerIds),
        managers: stateFilter.managers && deserializeBwicSelectFilter(stateFilter.managers.filter, filter.collateralManagers),
        dealName: stateFilter.dealName && deserializeBwicSelectFilter(stateFilter.dealName.filter, filter.dealName),
        closing: stateFilter.closing && deserializeDateFilter(filter.closingDate),
        maturity: stateFilter.maturity && deserializeDateFilter(filter.maturity),
        reinvestmentEnd: stateFilter.reinvestmentEnd && deserializeDateFilter(filter.reinvestmentPeriodEnd, filter.reinvestmentPeriodEndDays),
        nonCallEnd: stateFilter.nonCallEnd && deserializeDateFilter(filter.nonCallPeriodEnd, filter.nonCallPeriodEndDays),
        vintage: stateFilter.vintage && deserializeDateFilter(filter.vintage),
        collateralType: stateFilter.collateralType && deserializeSelectFilter(stateFilter.collateralType.filter, filter.collateralTypes),
        amr: stateFilter.amr && deserializeBooleanFilter(filter.amrDeal),
        currency: stateFilter.currency && deserializeSelectFilter(stateFilter.currency.filter, filter.currencyCodes),
        euCompliance: stateFilter.euCompliance && deserializeBooleanFilter(filter.euCompliance),
        trustees: stateFilter.trustees && deserializeBwicSelectFilter(stateFilter.trustees.filter, filter.trustees),
        esg: stateFilter.esg && deserializeBooleanFilter(filter.esg),
        staticDeal: stateFilter.staticDeal && deserializeBooleanFilter(filter.staticDeal),
        outOfNC: stateFilter.outOfNC && deserializePeriodEnd(stateFilter.outOfNC?.filter, filter.outOfNC),
        outOfNCRange: stateFilter.outOfNCRange && deserializeRangeFilter(filter.outOfNCRange),
        outOfRI: stateFilter.outOfRI && deserializePeriodEnd(stateFilter.outOfRI?.filter, filter.outOfRI),
        outOfRIRange: stateFilter.outOfRIRange && deserializeRangeFilter(filter.outOfRIRange),
        color: stateFilter.color && deserializeSelectFilter(stateFilter.color.filter, filter.color),
        tradedColor: stateFilter.tradedColor && deserializeRangeFilter(filter.tradedColor),
    };
}

export const deserializeLoanMonitorFilter = (commonFilter: UserFilterValues, stateCommonFilter: CommonFilter) => {
    const filter = commonFilter as LoanUserFilterValues;
    const stateFilter = stateCommonFilter as LoanMonitorSavedFilter;
    const isCollateralManager = user.hasRoles(roles.CollateralManager);

    return {
        statuses: stateFilter.statuses && deserializeSelectFilter(stateFilter.statuses.filter, filter.loanStatuses),
        ratings: stateFilter.ratings && deserializeSelectFilter(stateFilter.ratings.filter, filter.ratings),
        cfRatings: stateFilter.cfRatings && deserializeSelectFilter(stateFilter.cfRatings.filter, filter.cfRatings),
        currency: stateFilter.currency && deserializeSelectFilter(stateFilter.currency.filter, filter.currencyCodes),
        maturity: stateFilter.maturity && deserializeDateFilter(filter.maturity),
        closing: stateFilter.closing && deserializeDateFilter(filter.closingDate),
        review: isCollateralManager && stateFilter.review && deserializeSelectFilter(stateFilter.review.filter, filter.review),
        analyst: isCollateralManager && stateFilter.analyst && deserializeSelectFilter(stateFilter.analyst.filter, filter.analystUserIds),
        arranger: stateFilter.arranger && deserializeSelectFilter(stateFilter.arranger.filter, filter.arrangerReferenceNames),
        coArranger: stateFilter.coArranger && deserializeSelectFilter(stateFilter.coArranger.filter, filter.coArrangerReferenceNames),
        covLite: stateFilter.covLite && deserializeBooleanFilter(filter.covLite),
        dataroom: stateFilter.dataroom && deserializeSelectFilter(stateFilter.dataroom.filter, filter.dataroom),
        due: stateFilter.due && deserializeDateTimeFilter(filter.due),
        industry: stateFilter.industry && deserializeSelectFilter(stateFilter.industry.filter, filter.industries),
        useOfProceeds: stateFilter.useOfProceeds && deserializeSelectFilter(stateFilter.useOfProceeds.filter, filter.useOfProceeds),
        refIndex: stateFilter.refIndex && deserializeRadioFilter(stateFilter.refIndex.filter, filter.refIndex),
        // Term is persisted as a days range (see serializeDaysFilter), so it must be passed
        // as the "days" argument - the same way nonCallEnd/reinvestmentEnd pass their *Days values.
        term: stateFilter.term && deserializeDateFilter(undefined, filter.term),
    };
}

function deserializeBwicSelectFilter<T = string>(
    stateFilterOptions?: FilterOption<T>[],
    savedFilterValues?: T[]): FilterOption<T>[] | undefined {
    if (!savedFilterValues?.length) return undefined;
    if (stateFilterOptions?.length) return deserializeSelectFilter(stateFilterOptions, savedFilterValues);

    return savedFilterValues.map(value => ({
        value,
        text: (value as unknown as string),
        selected: true,
        visible: true,
        disabled: false
    }));
}
